import React from 'react'
import {
  Flex,
  FormControl,
  FormLabel,
  Input,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Container,
  InputGroup,
  InputRightElement,
  Divider,
  Card,
  CardBody
} from '@chakra-ui/react';
import { EmailIcon, LockIcon, ViewIcon, ViewOffIcon, WarningIcon } from '@chakra-ui/icons';
import { validateLoginForm } from '../../utils/validation';
import toastService from '../../utils/toastService';

const SignInBoxNew = ({ onNameChange, onPasswordChange, checkSignIn, onRouteChange, loading }) => {
  const [showPassword, setShowPassword] = React.useState(false)
  const [username, setUsername] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [validationErrors, setValidationErrors] = React.useState({})
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = React.useState(false)

  const handleFieldChange = (field, value) => {
    if (field === 'username') {
      setUsername(value)
      onNameChange(value)
    } else if (field === 'password') {
      setPassword(value)
      onPasswordChange(value)
    }
    
    // Clear specific field error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = () => {
    setHasAttemptedSubmit(true)
    const validation = validateLoginForm(username, password)
    setValidationErrors(validation.errors)
    
    if (!validation.isValid) {
      toastService.validationError(
        'Please fix the errors below',
        'All fields must be filled correctly before signing in.'
      );
      return;
    }

    checkSignIn()
  }
  
  return (
    <Flex minH="100vh" bg="gray.50">
      <Container maxW="md" py={{ base: 8, md: 12 }} px={{ base: 4, md: 6 }}>
        <VStack spacing={8} align="stretch">
          <VStack spacing={4} textAlign="center">
            <Heading 
              fontSize={{ base: '2xl', md: '4xl' }}
              fontWeight="800"
              bgGradient="linear(to-r, brand.600, purple.600)"
              bgClip="text"
              textAlign="center"
            >
              Welcome to Grow
            </Heading>
            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" textAlign="center">
              Sign in to continue your learning journey
            </Text>
          </VStack>

          <Card shadow="xl" border="1px" borderColor="gray.200">
            <CardBody p={{ base: 6, md: 8 }}>
              <VStack spacing={6}>
                <FormControl isInvalid={validationErrors.username}>
                  <FormLabel color="gray.700" fontWeight="600">Username</FormLabel>
                  <HStack spacing={3}>
                    <Icon as={EmailIcon} color="gray.400" boxSize={5} />
                    <Input 
                      type="text"
                      placeholder="Enter your username"
                      size={{ base: 'md', md: 'lg' }}
                      borderRadius="lg"
                      borderColor={validationErrors.username ? "red.300" : "gray.300"}
                      _hover={{ borderColor: validationErrors.username ? "red.400" : "gray.400" }}
                      _focus={{ borderColor: validationErrors.username ? "red.500" : "brand.500", boxShadow: validationErrors.username ? "0 0 0 1px var(--chakra-colors-red-500)" : "0 0 0 1px var(--chakra-colors-brand-500)" }}
                      onChange={(event) => handleFieldChange('username', event.target.value)}
                      flex={1}
                    />
                  </HStack>
                  {validationErrors.username && (
                    <HStack mt={2} spacing={2}>
                      <Icon as={WarningIcon} color="red.500" boxSize={3} />
                      <Text fontSize="sm" color="red.600">{validationErrors.username}</Text>
                    </HStack>
                  )}
                </FormControl>
                
                <FormControl isInvalid={validationErrors.password}>
                  <FormLabel color="gray.700" fontWeight="600">Password</FormLabel>
                  <HStack spacing={3}>
                    <Icon as={LockIcon} color="gray.400" boxSize={5} />
                    <InputGroup flex={1}>
                      <Input 
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        size={{ base: 'md', md: 'lg' }}
                        borderRadius="lg"
                        borderColor={validationErrors.password ? "red.300" : "gray.300"}
                        _hover={{ borderColor: validationErrors.password ? "red.400" : "gray.400" }}
                        _focus={{ borderColor: validationErrors.password ? "red.500" : "brand.500", boxShadow: validationErrors.password ? "0 0 0 1px var(--chakra-colors-red-500)" : "0 0 0 1px var(--chakra-colors-brand-500)" }}
                        onChange={(event) => handleFieldChange('password', event.target.value)}
                        pr="3rem"
                      />
                      <InputRightElement width="3rem">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <Icon as={showPassword ? ViewOffIcon : ViewIcon} color="gray.400" />
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </HStack>
                  {validationErrors.password && (
                    <HStack mt={2} spacing={2}>
                      <Icon as={WarningIcon} color="red.500" boxSize={3} />
                      <Text fontSize="sm" color="red.600">{validationErrors.password}</Text>
                    </HStack>
                  )}
                  <Text
                    fontSize="sm"
                    color="brand.600"
                    textAlign="right"
                    cursor="pointer"
                    mt={2}
                    _hover={{ textDecoration: 'underline' }}
                    onClick={() => onRouteChange('forgotPassword')}
                  >
                    Forgot password?
                  </Text>
                </FormControl>

                <VStack spacing={4} w="full">
                  <Button
                    size={{ base: 'md', md: 'lg' }}
                    w="full"
                    colorScheme="brand"
                    isLoading={loading}
                    loadingText="Signing in..."
                    onClick={handleSubmit}
                    isDisabled={loading || (hasAttemptedSubmit && Object.keys(validationErrors).length > 0)}
                  >
                    Sign In
                  </Button>
                  
                  <HStack>
                    <Divider />
                    <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">or</Text>
                    <Divider />
                  </HStack>
                  
                  <Button
                    size={{ base: 'md', md: 'lg' }}
                    w="full"
                    variant="outline"
                    colorScheme="brand"
                    onClick={() => onRouteChange('register')}
                  >
                    Create New Account
                  </Button>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Flex>
  );
};

const RegisterBoxNew = ({ onNameChange, onEmailChange, onPasswordChange, onRegister, onRouteChange, loading }) => {
  const [showPassword, setShowPassword] = React.useState(false)
  
  return (
    <Flex minH="100vh" bg="gray.50">
      <Container maxW="md" py={{ base: 8, md: 12 }} px={{ base: 4, md: 6 }}>
        <VStack spacing={8} align="stretch">
          <VStack spacing={4} textAlign="center">
            <Heading 
              fontSize={{ base: '2xl', md: '4xl' }}
              fontWeight="800"
              bgGradient="linear(to-r, brand.600, purple.600)"
              bgClip="text"
              textAlign="center"
            >
              Join Grow
            </Heading>
            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" textAlign="center">
              Start your personalized journey today
            </Text>
          </VStack>

          <Card shadow="xl" border="1px" borderColor="gray.200">
            <CardBody p={{ base: 6, md: 8 }}>
              <VStack spacing={6}>
                <FormControl>
                  <FormLabel color="gray.700" fontWeight="600">Username</FormLabel>
                  <HStack spacing={3}>
                    <Icon as={EmailIcon} color="gray.400" boxSize={5} />
                    <Input 
                      type="text"
                      placeholder="Choose a username"
                      size={{ base: 'md', md: 'lg' }}
                      borderRadius="lg"
                      _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                      onChange={(event) => onNameChange(event.target.value)}
                      flex={1}
                    />
                  </HStack>
                </FormControl>
                
                <FormControl>
                  <FormLabel color="gray.700" fontWeight="600">Email</FormLabel>
                  <HStack spacing={3}>
                    <Icon as={EmailIcon} color="gray.400" boxSize={5} />
                    <Input 
                      type="email"
                      placeholder="Enter your email address"
                      size={{ base: 'md', md: 'lg' }}
                      borderRadius="lg"
                      _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                      onChange={(event) => onEmailChange(event.target.value)}
                      flex={1}
                    />
                  </HStack>
                </FormControl>
                
                <FormControl>
                  <FormLabel color="gray.700" fontWeight="600">Password</FormLabel>
                  <HStack spacing={3}>
                    <Icon as={LockIcon} color="gray.400" boxSize={5} />
                    <InputGroup flex={1}>
                      <Input 
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create a secure password"
                        size={{ base: 'md', md: 'lg' }}
                        borderRadius="lg"
                        _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                        onChange={(event) => onPasswordChange(event.target.value)}
                        pr="3rem"
                      />
                      <InputRightElement width="3rem">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <Icon as={showPassword ? ViewOffIcon : ViewIcon} color="gray.400" />
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </HStack>
                </FormControl>

                <VStack spacing={4} w="full">
                  <Button
                    size={{ base: 'md', md: 'lg' }}
                    w="full"
                    colorScheme="brand"
                    isLoading={loading}
                    loadingText="Creating account..."
                    onClick={onRegister}
                  >
                    Create Account
                  </Button>
                  
                  <HStack>
                    <Divider />
                    <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">or</Text>
                    <Divider />
                  </HStack>
                  
                  <Button
                    size={{ base: 'md', md: 'lg' }}
                    w="full"
                    variant="outline"
                    colorScheme="brand"
                    onClick={() => onRouteChange('login')}
                  >
                    Sign In Instead
                  </Button>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Flex>
  );
};

const Authentication = ({ state, onNameChange, onEmailChange, onPasswordChange, checkSignIn, onRegister, onRouteChange, loading }) => {
  return (
    <>
      {state.route === 'login' ? (
        <SignInBoxNew
          onNameChange={onNameChange}
          onPasswordChange={onPasswordChange}
          checkSignIn={checkSignIn}
          onRouteChange={onRouteChange}
          loading={loading}
        />
      ) : (
        <RegisterBoxNew
          onNameChange={onNameChange}
          onEmailChange={onEmailChange}
          onPasswordChange={onPasswordChange}
          onRegister={onRegister}
          onRouteChange={onRouteChange}
          loading={loading}
        />
      )}
    </>
  );
};

export default Authentication;